import styled from '@emotion/styled';
import { captureException as sentryCaptureException } from '@sentry/react';
import { FormattedMessage } from 'react-intl';
import {
  BodyTextMedium,
  ComponentTextSmall,
  DeprecatedButton as Button,
  HeadingText30,
  HeadingText40,
  HeadingText50,
} from '@modernhealth/vital-components';
import React, { useEffect } from 'react';
import { Redirect, useParams } from 'react-router';
import { colors, breakpointValues } from '@modernhealth/vital-foundation';
import {
  SessionListItem,
  CircleHostCard,
  SeriesAccessType,
} from '@modernhealth/circles-web-components';
import { CircleSessionBookingStatus } from '@modernhealth/circles-graphql';
import moment from 'moment';

import { useTranslation } from 'src/shared/hooks/formatMessage';
import useShowSkeletonDelay from 'src/shared/hooks/useShowSkeletonDelay';
import { Head } from 'src/shared/components/Head';
import { BackButton } from 'src/shared/components/BackButton';
import { mediaQueryMinWidth } from 'src/shared/utils';
import { filterNullish } from 'src/shared/typeUtils/filterNullish';
import { captureException } from 'src/shared/utils/captureException';

import { useFetchTopicQuery } from '../documents/topic.generated';
import TopicPageSkeleton from './skeletons/TopicPageSkeleton';
import { formatSeriesAccessType } from '../utils/formatSeriesAccessType';
import { formatCircleFormat } from '../components/utils/formatCircleFormat';

const TopicInfoWrapper = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 30,
});

const PageWrapper = styled.div({
  [mediaQueryMinWidth(breakpointValues.md)]: {
    paddingRight: '25%',
    paddingLeft: '25%',
  },
});

const NoteText = styled(BodyTextMedium)`
  color: ${colors.gray75};
  font-weight: 600;
`;

interface TopicProps {
  topicSlug: string;
}

export enum SessionListItemTestIds {
  RsvpButton = 'SessionListItemTestIds.RsvpButton',
  WaitlistButton = 'SessionListItemTestIds.WaitlistButton',
}

const TopicPage: React.FC<TopicProps> = ({ topicSlug }) => {
  const { $t } = useTranslation();
  const { data, loading, error, refetch } = useFetchTopicQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      topicSlug,
    },
  });

  useEffect(() => {
    if (error) {
      sentryCaptureException(error);
      captureException(error);
    }
  }, [error]);

  const show = useShowSkeletonDelay(!!data);

  if (error) {
    return (
      <div css={{ display: 'flex', flexDirection: 'column' }}>
        <BodyTextMedium marginBottom={8} centered>
          Couldn't load page
        </BodyTextMedium>
        <Button
          title="Retry"
          onClick={() => {
            refetch();
          }}
          size="large"
        />
      </div>
    );
  }

  const waitListButtontitle = `${$t({ defaultMessage: 'Join waitlist' })}`;
  const registerButtonTitle = `${$t({ defaultMessage: 'Register' })}`;
  if (show || (loading && !data)) {
    return <TopicPageSkeleton />;
  }

  if (!data?.circleTopicBySlug) {
    // TODO: Error state
    return <Redirect to="/" />;
  }

  const topic = data.circleTopicBySlug;
  const { name, description, note, sessions, facilitators } = topic;

  const firstSession = sessions.edges.find(Boolean)?.node;
  const firstFacilitator = firstSession?.facilitators.find(Boolean);

  const sessionCount = sessions.edges.length;
  if (sessionCount && sessionCount >= 100) {
    captureException(`Circle sessions were truncated at 100!`, {
      extra: { count: sessionCount },
    });
  }

  const title = `${name} Sessions | Circles by Modern Health`;
  const shortDescription = `Join an upcoming ${name} session, part of Circles at Modern Health.`;
  const waitListUrl =
    'https://docs.google.com/forms/d/1W8WKBBGgm58jEOaZGPHX3aZfnZzq6ZjGfuUxA_eJOEY/edit';
  const hosts = filterNullish(facilitators.edges).map(host => host.node);
  const isMemberOnly = topic.series?.accessType === SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY;
  const sessionFormat = formatCircleFormat(topic.format);
  return (
    <PageWrapper>
      <Head>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta property="og:description" content={shortDescription} />
        {firstFacilitator?.photo && <meta property="og:image" content={firstFacilitator.photo} />}
      </Head>
      <BackButton />
      {isMemberOnly && (
        <ComponentTextSmall marginBottom={7} fontColor="violet55">
          {$t(formatSeriesAccessType(SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY))}
        </ComponentTextSmall>
      )}
      <HeadingText50 marginBottom={14}>{name}</HeadingText50>
      <TopicInfoWrapper>
        <BodyTextMedium>
          {sessionFormat}
          {` • ${topic.duration} min`}
        </BodyTextMedium>
      </TopicInfoWrapper>
      <BodyTextMedium fontColor="gray65" marginTop={20}>
        {description}
      </BodyTextMedium>
      <NoteText marginTop={20} marginBottom={48}>
        {note}
      </NoteText>
      {hosts.length > 0 && (
        <section css={{ marginBottom: 40 }}>
          <HeadingText40 marginBottom={26}>
            {' '}
            <FormattedMessage defaultMessage="Meet your hosts" />
          </HeadingText40>
          <div
            css={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))',
            }}
          >
            {hosts.map(host => (
              <CircleHostCard
                key={host.id}
                photo={host.photo}
                credential={host.credential}
                name={host.name}
                country={host.country}
              />
            ))}
          </div>
        </section>
      )}
      <HeadingText30 marginBottom={30}>
        <FormattedMessage defaultMessage="Attend a session" />
      </HeadingText30>
      {sessions.edges.map(session => {
        if (!session) {
          return null;
        }
        const sessionEndTime = moment(session.node.startTime)
          .clone()
          .add(topic.duration, 'minutes');
        const fullyBooked = session.node.bookingStatus === CircleSessionBookingStatus.FullyBooked;
        const providersNames = session.node.facilitators.map(host => host?.name ?? '');

        const registerButton = fullyBooked ? (
          <Button
            testID={SessionListItemTestIds.WaitlistButton}
            title={waitListButtontitle}
            variant="secondary"
            target="_blank"
            href={waitListUrl}
          />
        ) : (
          <Button
            testID={SessionListItemTestIds.RsvpButton}
            title={registerButtonTitle}
            variant="dark"
            target="_blank"
            to={`/circle/${topicSlug}/session/${session.node.id}/membership`}
          />
        );

        return (
          <SessionListItem
            key={session.node.id}
            providersNames={providersNames}
            startTime={session.node.startTime}
            endTime={sessionEndTime.toISOString()}
            bookingStatus={session.node.bookingStatus}
            primaryButton={registerButton}
          />
        );
      })}
    </PageWrapper>
  );
};

const TopicRoute: React.FC<unknown> = () => {
  const { topicSlug } = useParams<TopicProps>();

  if (!topicSlug) {
    return <Redirect to="/" />;
  }

  return <TopicPage topicSlug={topicSlug} />;
};

export default TopicRoute;
